import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { useCallback, useRef, useState } from "react";
import ExtendButton from "../live-view/MaximizeButton";
import CustomIconButton from "../button/CustomIconButton";

interface FullScreenViewProps {
  imageUrl: string;
  onClose: () => void;
}

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;

const POSITION_LIMITS = {
  x: { min: -500, max: 500 },
  y: { min: -500, max: 500 },
};

const FullScreenView = ({
  imageUrl,
  onClose,
}: FullScreenViewProps): JSX.Element => {
  const [isDragging, setIsDragging] = useState(false);
  const [isZoomInDisabled, setIsZoomInDisabled] = useState(false);
  const [isZoomOutDisabled, setIsZoomOutDisabled] = useState(false);

  const zoomRef = useRef(1);
  const positionRef = useRef({ x: 0, y: 0 });
  const imageRef = useRef<HTMLImageElement>(null);
  const theme = useTheme();

  const updateZoomButtons = (zoom: number) => {
    setIsZoomInDisabled(zoom >= ZOOM_MAX);

    setIsZoomOutDisabled(zoom <= ZOOM_MIN);
  };

  const updateImageTransform = useCallback(() => {
    if (imageRef.current) {
      imageRef.current.style.transform = `scale(${zoomRef.current}) translate(${positionRef.current.x}px, ${positionRef.current.y}px)`;
    }
  }, []);

  const handleWheel = (event: React.WheelEvent) => {
    const newZoom = Math.max(
      ZOOM_MIN,
      Math.min(ZOOM_MAX, zoomRef.current + event.deltaY * -0.001)
    );

    zoomRef.current = newZoom;

    updateImageTransform();

    updateZoomButtons(newZoom);
  };

  const handleMouseDown = () => {
    setIsDragging(true);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (!isDragging) return;

    const newX = positionRef.current.x + event.movementX;
    const newY = positionRef.current.y + event.movementY;

    positionRef.current = {
      x: Math.max(POSITION_LIMITS.x.min, Math.min(POSITION_LIMITS.x.max, newX)),
      y: Math.max(POSITION_LIMITS.y.min, Math.min(POSITION_LIMITS.y.max, newY)),
    };

    updateImageTransform();
  };

  const handleZoomIn = () => {
    const newZoom = Math.min(ZOOM_MAX, zoomRef.current + 0.25);

    zoomRef.current = newZoom;

    updateImageTransform();

    updateZoomButtons(newZoom);
  };

  const handleZoomOut = () => {
    const newZoom = Math.max(ZOOM_MIN, zoomRef.current - 0.25);

    zoomRef.current = newZoom;

    updateImageTransform();

    updateZoomButtons(newZoom);
  };

  return (
    <Modal open onClose={onClose}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: theme.palette.otherFullScreenBackground.main,
          overflow: "hidden",
          position: "relative",
        }}
        onWheel={handleWheel}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <Box sx={{ position: "absolute", zIndex: 3, right: 8, top: 8 }}>
          <ExtendButton title="Minimize" changeExpandedMode={onClose} />
        </Box>

        <Box
          sx={{
            position: "absolute",
            bottom: 16,
            right: 16,
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            padding: "8px",
            zIndex: 10,
          }}
        >
          <CustomIconButton onClick={handleZoomIn} disabled={isZoomInDisabled}>
            <AddIcon />
          </CustomIconButton>
          <CustomIconButton
            onClick={handleZoomOut}
            disabled={isZoomOutDisabled}
          >
            <RemoveIcon />
          </CustomIconButton>
        </Box>

        <Box
          component="img"
          src={imageUrl}
          alt="Full Screen"
          ref={imageRef}
          sx={{
            cursor: isDragging ? "grabbing" : "grab",
            width: "100%",
            height: "100%",
            objectFit: "contain",
            objectPosition: "center",
            transformOrigin: "center",
            userSelect: "none",
          }}
          onMouseDown={handleMouseDown}
          draggable={false}
        />
      </Box>
    </Modal>
  );
};

export default FullScreenView;
